import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AssignPermission, Complaint, ComplaintPosition, complaintPriorities, ComplaintPriorities, ComplaintsDocumentType, ComplaintsDocumentTypeDict, ComplaintsDocumentTypes, ComplaintUserType, DocumentStatus, PersonsService } from '@icc/b2b/shared';
import { DialogService, InputComponent } from '@icc/ui';
import { ComplaintParty } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { ComplaintUserPreviewDialogComponent } from '../complaint-user-preview-dialog/complaint-user-preview-dialog.component';
import { ConfigService, TranslateService } from '@icc/shared';
import { ComplaintChangeUserDialogComponent } from '../complaint-change-user-dialog/complaint-change-user-dialog.component';
import { ComplaintService } from '../complaint.service';
import { A, V } from '@angular/cdk/keycodes';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { Common, parseDate } from '@icc/shared/helpers';
import { NgxPermissionsService } from 'ngx-permissions';
import { ComplaintInfoComponent } from '../complaint-info/complaint-info.component';
import { ComplaintButton } from 'libs/b2b/shared/src/lib/models/complaint-workflow-rule';

@Component({
    selector: 'icc-complaint-summary',
    templateUrl: './complaint-summary.component.html',
    styleUrls: ['./complaint-summary.component.scss'],
})
export class ComplaintSummaryComponent implements OnInit, OnChanges {
    @Input() document!: Complaint;
    @Input() preview = false;
    @Input() positionsWorkflowsGroups: Map<number, ComplaintPosition[]> = new Map();
    @Input() positions: ComplaintPosition[] = [];
    @Input() selectedPositions: string[] = [];
    @Input() appointments: any[] = [];
    @Input() appointmentsAttachments: any[] = [];
    @Output() complaintTypeChange = new EventEmitter<ComplaintsDocumentType>();
    @Output() clearSelectedPositions = new EventEmitter<void>();

    hasAccessTo = {
        manageComplaint: false,
        appealToDistributor: false,
        appealToManufacturer: false,
        receivedFromManufacturer: false,
        receivedFromDistributor: false,
    };

    documentTypes = ComplaintsDocumentTypes;
    documentTypesDict = ComplaintsDocumentTypeDict;
    number = Number;

    documentPriorities = complaintPriorities;

    status: DocumentStatus | undefined;
    authorizedRetailerStatus: DocumentStatus | undefined;
    distributorStatus: DocumentStatus | undefined;
    manufacturerStatus: DocumentStatus | undefined;

    positionsGroups: Map<string, ComplaintPosition[]> = new Map();

    private subscriptions: Subscription[] = [];

    origin: 'list' | 'map' | 'appointment-list' | 'appointment-map' | null;

    navButtonLabel = this.translateService.instant('COMPLAINT|Powrót do listy');

    constructor(
        private dialogService: DialogService,
        private configService: ConfigService,
        private complaintService: ComplaintService,
        private personsService: PersonsService,
        private router: Router,
        private route: ActivatedRoute,
        private permissionsService: NgxPermissionsService,
        private translateService: TranslateService,
    ) {
        this.origin = this.route.snapshot.queryParamMap.get('origin') as 'list' | 'map' | 'appointment-list' | 'appointment-map' | null;
        this.subscriptions.push(
            this.permissionsService.permissions$.subscribe(() => {
                this.checkAccess();
            }),
        );
    }

    async ngOnInit(): Promise<void> {
        this.complaintService.loadContractors();
        await this.complaintService.contractorsLoaded;
        await this.personsService.loaded;
        await this.complaintService.workflowsLoaded;
        this.status = this.complaintService.statuses.find((s) => +s.id === +this.document.statusId);
        this.authorizedRetailerStatus = this.complaintService.statuses.find((s) => +s.id === +this.document.authorizedRetailerStatusId);
        this.distributorStatus = this.complaintService.statuses.find((s) => +s.id === +this.document.distributorStatusId);
        this.manufacturerStatus = this.complaintService.statuses.find((s) => +s.id === +this.document.manufacturerStatusId);
        this.positionsGroups = this.complaintService.groupPositions(this.positionsWorkflowsGroups);
        this.setNavButtonLabel();
    }

    async ngOnChanges(): Promise<void> {
        this.complaintService.loadContractors();
        await this.complaintService.contractorsLoaded;
        await this.personsService.loaded;
        this.status = this.complaintService.statuses.find((s) => +s.id === +this.document.statusId);
        this.authorizedRetailerStatus = this.complaintService.statuses.find((s) => +s.id === +this.document.authorizedRetailerStatusId);
        this.distributorStatus = this.complaintService.statuses.find((s) => +s.id === +this.document.distributorStatusId);
        this.manufacturerStatus = this.complaintService.statuses.find((s) => +s.id === +this.document.manufacturerStatusId);
        this.positionsGroups = this.complaintService.groupPositions(this.positionsWorkflowsGroups);
        this.checkAccess();
        this.updateButtonAvailability();
    }

    async checkAccess() {
        this.hasAccessTo.manageComplaint = await this.permissionsService.hasPermission('complaints-manage') && ((this.document.assignedTo && this.document.assignedTo.startsWith(this.getUserType())) || await this.permissionsService.hasPermission('complaints-manage-manufacturer'));
    }

    openChangeUserDialog(userType: string) {
        const user = this.getUserByType(userType);

        this.dialogService
            .open(ComplaintChangeUserDialogComponent, {
                data: {
                    user,
                    userType,
                    companyId: this.configService.user?.user?.ActiveCompanyLicenseType === 'license'
                        ? userType === 'authorizedRetailer'
                            ? this.document.distributorCompanyId
                            : this.configService.user?.company?.id
                        : this.document.distributorCompanyId,
                    chooseContractorContractor:
                        this.configService.user?.user?.ActiveCompanyLicenseType === 'license' &&
                        userType === 'authorizedRetailer',
                },
            })
            .afterClosed()
            .subscribe((data) => {
                if (data) {
                    this.complaintService.changeUser(this.document, data, userType);
                }
            });
    }

    getUserByType(userType: string): ComplaintParty | undefined {
        return this.complaintService.getUserByType(userType, this.document);
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    getPriority() {
        const rawPriority = this.complaintService.getPriority(this.document);
        if (rawPriority) {
            const priorityGroup = ComplaintPriorities[+rawPriority.slice(0, 1)];
            const priorityValue = rawPriority.slice(1);
            return this.translateService.instant(priorityGroup) + '-' + priorityValue;
        }
        return '';
    }

    getAssignedTo() {
        return this.complaintService.getAssignedTo(this.document, this.document.assignedTo, this.document.assignedCompanyId);
    }

    getAssignToValue() {
        if (this.document.assignedTo === 'distributor') {
            return 'distributor-' + this.document.assignedCompanyId;
        }
        return this.document.assignedTo;
    }

    getResponsibleUser() {
        return this.document?.[this.getUserType()]?.referenceName ?? '';
    }

    getAssignedUserName() {
        return this.complaintService.getAssignedToUserName(this.document, this.getUserType());
    }

    getAssignedUser() {
        return (
            this.personsService.getWorkerByUserId(
                this.document[
                    this.getUserType() + 'UserId' as
                        | 'authorizedRetailerUserId'
                        | 'distributorUserId'
                        | 'manufacturerUserId'
                ] ?? 0
            )?.person?.reference_name ?? ''
        );

    }

    backToList() {
        let target = 'complaints';
        if (this.origin === 'appointment-list' || this.origin === 'appointment-map') {
            target = 'appointments';
        }
        const commands = ['trade', 'complaint', 'list', target];
        this.router.navigate(commands, { queryParams: { origin: this.origin } });
    }

    acceptChanges() {
        this.complaintService.acceptChanges(this.document, this.positionsWorkflowsGroups);
    }

    movePositionsToNewDocument() {
        const positions = this.positions.filter((p) => this.selectedPositions.includes(p.uuid));
        this.complaintService.movePositionsToNewDocument(this.document, this.positions, this.selectedPositions);
        this.clearSelectedPositions.emit();
    }

    showInfo() {
        this.dialogService.open(ComplaintInfoComponent, {
            data: {
                info: this.translateService.instant('COMPLAINT|Podział jest wymagany ze względu na różne procesy rozpatrywania reklamacji lub ze względu na różne numery faktury/zamówienia/seryjny. WAŻNE! Przed dokonaniem podziału upewnij się, że wszystkie numery faktury/zamówienia/seryjny są właściwe i czy miejsca wystąpienia usterki są prawidłowe.'),
            },
        });
    }

    splitNeeded() {
        return this.positionsGroups.size > 1;
    }

    setNavButtonLabel() {
        if (this.origin === 'list' || this.origin === 'appointment-list') {
            this.navButtonLabel = this.translateService.instant('COMPLAINT|Powrót do listy');
        } else if (this.origin === 'map' || this.origin === 'appointment-map') {
            this.navButtonLabel = this.translateService.instant('COMPLAINT|Powrót do mapy');
        }
    }

    findClosestAppointmentDate() {
        const now = new Date();
        now.setDate(now.getDate() - 1);
        const futureAppointmentsForDocument = this.appointments.filter(appointment =>
            !appointment.isDeleted &&
            new Date(appointment.date).getTime() >= now.getTime() &&
            (appointment.status !== 'canceled' && appointment.status !== 'completed') &&
            !appointment.servicemanSignatureImage
        );

        if (futureAppointmentsForDocument.length === 0) {
            return null;
        }

        const closestAppointment = futureAppointmentsForDocument.reduce((closest, current) => {
            const closestDateDiff = Math.abs(new Date(closest.date).getTime() - now.getTime());
            const currentDateDiff = Math.abs(new Date(current.date).getTime() - now.getTime());
            return currentDateDiff < closestDateDiff ? current : closest;
        });

        return closestAppointment.date;
    }

    workflowButtonClicked(button: ComplaintButton) {
        this.complaintService.triggerWorkflow('button_click', this.document, {
            button,
        }, this.positionsWorkflowsGroups);
    }

    updateButtonAvailability() {
        if (this.positionsWorkflowsGroups.size === 1) {
            const distributorDocumentStatusId = this.complaintService.getDocumentStatusForUser(this.document, 'distributor');
            const manufacturerDocumentStatusId = this.complaintService.getDocumentStatusForUser(this.document, 'manufacturer');
            const positionsWorkflow = this.complaintService.workflows.find(
                (el) => +el.id === +this.positionsWorkflowsGroups.keys().next().value
            );
            const distributorRolesStatuses = (positionsWorkflow?.userRolesStatuses || []).filter((el) =>
                el.level === 'distributor' &&
                this.getUserType() === 'authorizedRetailer' &&
                el.userRolesIds.some(
                    (roleId) =>
                    (Number(roleId) === 0 ||
                        this.configService.user.user?.Roles.map(Number).includes(
                            Number(roleId)
                        ))
                )
            );
            const manufacturerRolesStatuses = (positionsWorkflow?.userRolesStatuses || []).filter((el) =>
                el.level === 'manufacturer' &&
                this.getUserType() === 'distributor' &&
                el.userRolesIds.some(
                    (roleId) =>
                    (Number(roleId) === 0 ||
                        this.configService.user.user?.Roles.map(Number).includes(
                            Number(roleId)
                        ))
                )
            );
            if (!distributorRolesStatuses.length) {
                this.hasAccessTo.appealToDistributor = false;
                this.hasAccessTo.receivedFromDistributor = false;
            } else {
                this.hasAccessTo.appealToDistributor = this.getUserType() === 'authorizedRetailer' &&distributorRolesStatuses.some(
                    el => el.buttons[distributorDocumentStatusId]?.appeal_to_distributor?.available
                );
                this.hasAccessTo.receivedFromDistributor = this.getUserType() === 'authorizedRetailer' &&distributorRolesStatuses.some(
                    el => el.buttons[distributorDocumentStatusId]?.received_from_distributor?.available
                );
            }

            if (!manufacturerRolesStatuses.length) {
                this.hasAccessTo.appealToManufacturer = false;
                this.hasAccessTo.receivedFromManufacturer = false;
            } else {
                this.hasAccessTo.receivedFromManufacturer = this.getUserType() === 'distributor' && manufacturerRolesStatuses.some(
                    el => el.buttons[manufacturerDocumentStatusId]?.received_from_manufacturer?.available
                );
                this.hasAccessTo.appealToManufacturer = this.getUserType() === 'distributor' && manufacturerRolesStatuses.some(
                    el => el.buttons[manufacturerDocumentStatusId]?.appeal_to_manufacturer?.available
                );
            }
        } else {
            this.hasAccessTo.appealToDistributor = false;
            this.hasAccessTo.receivedFromDistributor = false;
            this.hasAccessTo.appealToManufacturer = false;
            this.hasAccessTo.receivedFromManufacturer = false;
        }
    }
}
